import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "./Reveal";
import Countdown from "./Countdown";

const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="hero" ref={ref} className="relative lg:min-h-screen flex items-center bg-[#FFFFFF] pt-24 pb-12 sm:pb-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center w-full">
        <div className="lg:col-span-7 relative z-10">
          <MaskLine delay={0.1}>
            <span data-testid="hero-eyebrow" className="text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-[#1B2FD8]">
              HERcules Health | Back To You
            </span>
          </MaskLine>
          <h1 data-testid="hero-headline" className="font-display font-extrabold tracking-tight leading-[0.95] text-[#0B0B12] mt-6">
            <MaskLine delay={0.25}><span className="text-3xl sm:text-5xl lg:text-6xl">FREE 14-DAY</span></MaskLine>
            <MaskLine delay={0.4}><span className="text-[3.4rem] sm:text-7xl lg:text-8xl text-[#4B2A9E]">METABOLIC</span></MaskLine>
            <MaskLine delay={0.55}><span className="text-[3.4rem] sm:text-7xl lg:text-8xl italic text-[#1B2FD8]">RESET</span></MaskLine>
          </h1>
          <motion.div
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-6 sm:mt-8 text-sm md:text-lg text-[#4A4A66] leading-relaxed max-w-xl space-y-4 text-center mx-auto lg:mx-0"
          >
            <p>CLEAN EATING CHALLENGE THAT WILL CHANGE HOW YOU LOOK AT &ldquo;DIETING&rdquo; FOREVER.</p>
            <p>YOUR KIDS ARE BACK IN SCHOOL. NOW IT&rsquo;S TIME TO GET BACK TO YOU.</p>
            <p className="text-[#0B0B12] font-semibold">Eat more. Exercise less. Get your body responding again.</p>
            <p data-testid="hero-weight-loss-line" className="text-[#0B0B12] font-semibold">2–10 LBS commonly lost in 14 days.</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-8 flex flex-wrap items-center gap-4 sm:gap-5"
          >
            <button
              data-testid="hero-join-reset-button"
              onClick={() => scrollToId("register")}
              className="group w-full sm:w-auto rounded-full bg-[#1B2FD8] text-[#FFFFFF] px-8 py-4 font-display font-bold tracking-wide hover:bg-[#0B0B12] transition-colors duration-300"
            >
              JOIN THE RESET <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
            </button>
            <span data-testid="hero-event-dates-badge" className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-[#4B2A9E] border border-[#4B2A9E]/30 rounded-full px-4 py-2">
              FREE &nbsp;|&nbsp; SEPT 28 – OCT 11
            </span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="mt-8 pt-6 sm:pt-8 border-t border-[#0B0B12]/10"
          >
            <Countdown />
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-t-[999px] rounded-b-3xl border border-[#0B0B12]/10 shadow-2xl shadow-[#0B0B12]/20 max-w-sm mx-auto lg:max-w-none"
          >
            <motion.img
              src="/images/hero.jpg"
              alt="Woman standing strong in nature — Back To You"
              data-testid="hero-image"
              fetchPriority="high"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-[400px] sm:h-[540px] object-cover"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
